import { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  computeWeeklyTargets,
  programWeeklyPrescription,
  type AthleteTargets,
  type CoachAthleteCard,
  type CurrentMax,
  type Program,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { Button } from "../primitives/Button";
import { useBreakpoint } from "../useBreakpoint";
import { AlertsPanel } from "../Progress/AlertsPanel";
import { DensityTrendPanel } from "../Progress/DensityTrendPanel";
import { buildMuscleCoverage } from "../Progress/muscleCoverage";
import { MuscleCoverageSection } from "../Progress/MuscleCoverageSection";
import { E1RMTrendsPanel } from "../Progress/E1RMTrendsPanel";
import { MovementBalancePanel } from "../Progress/MovementBalancePanel";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import { useSessionResultsFromDeps } from "../Progress/useSessionResults";
import type { ProgressDeps } from "../Progress/types";
import { AthleteCardRow } from "./AthleteCardRow";
import { CoachMaxesCard } from "./CoachMaxesCard";
import { CoachRhythmCard } from "./CoachRhythmCard";
import { CoachTargetsPanel } from "./CoachTargetsPanel";
import { EffortCoveragePanel } from "./EffortCoveragePanel";

// <AthleteDetail> (CO-9) — the coach's per-athlete drill-down. Every panel
// RE-EXPOSES the athlete's own surfaces over the CO-11 read path — the shared
// Progress panels render VERBATIM against an athlete-scoped ProgressDeps (same
// math, same i18n, same a11y as the athlete's Progress tab), so nothing here
// can drift from what the athlete sees:
//   • identity header = the SAME AthleteCardRow the dashboard list renders
//     (adherence/flags come from the dashboard-computed card — never re-derived)
//   • alerts (CO-1) = <AlertsPanel>, full-bleed above the coverage hero — the
//     athlete's own IA (ProgressView mounts it first, outside the panel grid),
//     and the one surface here that says DO something rather than reporting a
//     number. Pain never reaches it on this path: ProgressDeps.painMap is
//     host-injected and the coach host hardwires `{}` (AthleteDetailScreen),
//     because no coach-readable route carries painMap (it lives on Profile,
//     behind pickCoachProfileFields). So a spike the athlete sees escalated to
//     "back off" reads to the coach as a plain spike — the privacy floor
//     degrades the alert, it never leaks the reason.
//   • muscle coverage = buildMuscleCoverage + <MuscleCoverageSection>
//     (ProgressView's exact idiom, prescription from the athlete's pinned block)
//   • strength = <E1RMTrendsPanel>, balance = <MovementBalancePanel>,
//     volume = <DensityTrendPanel>
//   • rhythm = computeWeeklyCounts over the same session-results snapshot
//   • effort coverage (CO-10) = <EffortCoveragePanel>, the one panel with no
//     athlete-side twin: it needs per-set RIR, which no SessionResult carries,
//     so it is the only panel that fetches sets of its own
//   • current MAXes = read-only list (the edit surface, MaxesView, is
//     deliberately NOT mounted — coach writes are CO-7, a separate feature)
// Health panels are deliberately absent until the per-metric consent gate
// exists (backlog CO-9 note); the read path already strips them server-side.

export type AthleteDetailProps = {
  theme: UiTheme;
  /** The dashboard-computed summary card for this athlete (single source of
   *  adherence/flags/pinned-block — buildCoachDashboard, never re-derived). */
  card: CoachAthleteCard;
  /** Athlete-scoped ProgressDeps: apiFetch wrapped by makeAthleteScopedApiFetch,
   *  a FRESH per-athlete resultsCache, and the athlete's own units. */
  deps: ProgressDeps;
  programs: ReadonlyArray<Program>;
  currentProgramId?: string | undefined;
  weekStartDay?: number | undefined;
  /** Current MAX map (fetchMaxesDetailed with athleteSub); null while loading. */
  maxes: Map<string, CurrentMax> | null;
  /** The athlete's own weekly targets (AthleteTargets doc). ME-3: the target
   *  source, scored against the athlete's training; the pinned program stays
   *  only for the block name + the prescription 2nd reference. */
  athleteTargets: AthleteTargets | null;
  onBack: () => void;
  /** Open the coach targets editor (host swaps it in outside its ScrollView).
   *  Absent → the panel never offers «Edit». The server (PATCH /me/targets →
   *  403 no_write_grant) is the authority on whether the write lands, so «Edit»
   *  shows whenever this is wired. */
  onEditTargets?: () => void;
};

export function AthleteDetail({
  theme,
  card,
  deps,
  programs,
  currentProgramId,
  weekStartDay,
  maxes,
  athleteTargets,
  onBack,
  onEditTargets,
}: AthleteDetailProps) {
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const { wide } = useBreakpoint();
  const styles = makeStyles(theme);

  // ONE fetch pair for every panel below: coverage reads the snapshot here, and
  // every mounted panel reads the same single-flight cache instance.
  const { results, catalog } = useSessionResultsFromDeps(deps);

  const pinned = useMemo<Program | null>(
    () =>
      (currentProgramId
        ? programs.find((p) => p.id === currentProgramId)
        : null) ?? null,
    [programs, currentProgramId],
  );
  const prescription = useMemo(
    () =>
      pinned && catalog ? programWeeklyPrescription(pinned, catalog) : null,
    [pinned, catalog],
  );
  const coverage = useMemo(
    () =>
      results && catalog
        ? buildMuscleCoverage(
            results,
            catalog,
            pinned,
            prescription,
            Date.now(),
            weekStartDay,
            athleteTargets,
          )
        : null,
    [results, catalog, pinned, prescription, weekStartDay, athleteTargets],
  );
  // CO-7 / ME-3 — the full weekly-targets score (every axis, not the muscle-only
  // coverage census) for the read-only targets panel. Same inputs as coverage,
  // so it rides the SAME session-results snapshot — no extra fetch. The TARGET
  // source is the athlete's own AthleteTargets (host-fetched); the pinned
  // program stays only for the block name + prescription 2nd reference, so the
  // panel no longer needs a pinned program to render.
  const weeklyTargets = useMemo(
    () =>
      results && catalog
        ? computeWeeklyTargets(
            results,
            catalog,
            pinned,
            Date.now(),
            prescription,
            { weekStartDay },
            athleteTargets,
            locale,
          )
        : null,
    [
      pinned,
      results,
      catalog,
      prescription,
      weekStartDay,
      athleteTargets,
      locale,
    ],
  );
  // Resolve exercise-axis row labels BY SLUG against the live catalog (never a
  // write-time snapshot) — the shared resolver Progress already uses.
  const resolveExerciseName = useExerciseNameResolver(catalog);

  const panels: Array<[string, React.ReactNode]> = [
    ["e1rm", <E1RMTrendsPanel {...deps} />],
    ["balance", <MovementBalancePanel {...deps} />],
    // Volume (CO-1). Same relative slot as the athlete's own tab, where
    // density follows balance in the detail group. Reads the shared
    // session-results snapshot — no new route, no extra fetch.
    ["density", <DensityTrendPanel {...deps} />],
    [
      "effort",
      <EffortCoveragePanel
        theme={theme}
        apiFetch={deps.apiFetch}
        catalog={catalog}
      />,
    ],
    ["rhythm", <CoachRhythmCard theme={theme} results={results} />],
    [
      "maxes",
      <CoachMaxesCard
        theme={theme}
        maxes={maxes}
        catalog={catalog}
        units={deps.units}
      />,
    ],
  ];

  return (
    <View style={styles.wrap}>
      <View style={styles.backRow}>
        <Button
          theme={theme}
          variant="secondary"
          icon="chevron-left"
          label={t("coach.detail.back")}
          onPress={onBack}
        />
      </View>

      <AthleteCardRow theme={theme} card={card} />

      <AlertsPanel {...deps} />

      {coverage ? (
        <MuscleCoverageSection
          theme={theme}
          coverage={coverage}
          units={deps.units}
          locale={locale}
          figure={deps.figure}
          isDark={deps.isDark}
        />
      ) : null}

      {/* CO-7 / ME-3 — full-bleed, same relative slot as the athlete's own Home
          meter, above the panel grid. The targets are the athlete's own now, so
          this renders whenever there is something to score (the athlete has
          trained), independent of a pinned program; computeWeeklyTargets → null
          only when the athlete has no trained muscles at all. */}
      <CoachTargetsPanel
        theme={theme}
        data={weeklyTargets}
        units={deps.units}
        locale={locale}
        resolveExerciseName={resolveExerciseName}
        // «Edit» shows whenever the host wired the editor (i.e. the coach
        // detail). The SERVER is the authority on whether the write lands:
        // PATCH /me/targets 403s no_write_grant when the athlete hasn't granted,
        // and the editor maps that to a clear message. An earlier client-side
        // grant gate duplicated that authority and hid «Edit» deterministically
        // even with the grant live — server-authoritative is correct + robust.
        canEdit={!!onEditTargets}
        onEdit={() => onEditTargets?.()}
      />

      {wide ? (
        <View style={styles.grid}>
          {panels.map(([k, node]) => (
            <View key={k} style={styles.gridCell}>
              {node}
            </View>
          ))}
        </View>
      ) : (
        panels.map(([k, node]) => <View key={k}>{node}</View>)
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s4, padding: s.s4 },
    backRow: { flexDirection: "row" },
    // flexWrap 2-col on wide, single column on narrow. flex-start so a tall
    // card can't stretch its row sibling into an empty void.
    // ⚠️ NOT the same rules as ProgressView's panel grid (space-between +
    // rowGap + a strict width:48%), despite the identical wide?grid:column
    // mounting shape — the two DIVERGE deliberately. Registered as CN-panel-grid
    // in next/consolidation.md; collapsing them changes one live surface's
    // layout, so it needs Antenore's call, not a boy-scout merge.
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s3,
      alignItems: "flex-start",
    },
    gridCell: { flexGrow: 1, flexBasis: "48%", minWidth: 320 },
  });
}
